import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronDown, LogOut, Menu, PanelLeftClose, PanelLeftOpen, User } from 'lucide-react';
import { useLogoutMutation, useSession } from '@/lib/auth';
import { useMobileSidebar } from '@/lib/mobile-sidebar';
import { roleLabelTh } from '@/lib/role-label';
import { useSidebarCollapsed } from '@/lib/sidebar-state';
import { ThemeToggle } from './theme-toggle';

export function Header() {
  const { user } = useSession();
  const logout = useLogoutMutation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const { setOpen: setMobileOpen } = useMobileSidebar();

  const handleLogout = async () => {
    try {
      await logout.mutateAsync();
    } finally {
      await navigate({ to: '/login' });
    }
  };

  if (!user) return null;

  return (
    <header className="sticky top-0 z-10 flex h-14 items-center justify-between border-b border-[#E8E8EC] bg-white px-6 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center gap-3">
        {/* Mobile hamburger — opens drawer */}
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="เปิดเมนู"
          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 sm:hidden dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-50"
        >
          <Menu className="h-5 w-5" />
        </button>
        {/* Desktop collapse toggle */}
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'ขยาย sidebar' : 'ย่อ sidebar'}
          className="hidden rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 sm:flex dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-50"
        >
          {collapsed ? (
            <PanelLeftOpen className="h-5 w-5" />
          ) : (
            <PanelLeftClose className="h-5 w-5" />
          )}
        </button>
        <div className="text-sm text-slate-500 dark:text-slate-400">ระบบจัดการเนื้อหาข่าว</div>
      </div>
      <div className="flex items-center gap-1">
        {/* Cmd/Ctrl+K hint — purely informational; the keyboard handler in
            CommandPalette owns the actual shortcut. */}
        <div className="mr-1 hidden items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-500 sm:flex dark:border-slate-800 dark:text-slate-400">
          <kbd className="font-mono">⌘</kbd>
          <kbd className="font-mono">K</kbd>
          <span className="ml-1">ค้นหาเมนู</span>
        </div>
        <ThemeToggle />
        <details className="group relative">
          <summary className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 [&::-webkit-details-marker]:hidden [&::marker]:hidden">
            <div className="bg-brand-600 flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white">
              {user.name.slice(0, 1).toUpperCase()}
            </div>
            <div className="hidden text-left sm:block">
              <div className="font-medium text-slate-900 dark:text-slate-50">{user.name}</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                {roleLabelTh(user.role.slug)}
              </div>
            </div>
            <ChevronDown className="h-4 w-4 text-slate-400 group-open:rotate-180" />
          </summary>
          <div className="absolute right-0 top-full mt-1 w-56 rounded-md border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-800 dark:bg-slate-900">
            <div className="border-b border-slate-100 px-3 py-2 dark:border-slate-800">
              <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                {user.name}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">@{user.username}</div>
              <div className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                {user.permissions.length} สิทธิ์
              </div>
            </div>
            <Link
              to="/account"
              className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <User className="h-4 w-4" />
              บัญชีของฉัน
            </Link>
            <button
              type="button"
              onClick={() => {
                void handleLogout();
              }}
              disabled={logout.isPending}
              className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <LogOut className="h-4 w-4" />
              {logout.isPending ? 'กำลังออกจากระบบ...' : 'ออกจากระบบ'}
            </button>
          </div>
        </details>
      </div>
    </header>
  );
}
